import {
  Box,
  Grid,
  Paper,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import axiosClient from "../../api/axiosClient";
import Layout from "../../components/Layout";
import { UserRequest } from "./UserRequest";
import { UserResponse } from "./UserResponse";
import EditIcon from "@mui/icons-material/Edit";
import { UserDto } from "./UserDto";
import { searchGridSx } from "../../utils/responsiveStyles";

export default function User() {
  // Table state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState([]);

  // Dropdown roles
  const [roles, setRoles] = useState([]);

  // Search filters
  const [roleId, setRoleId] = useState(null);
  const [email, setEmail] = useState(null);
  const [username, setUsername] = useState(null);
  const [status, setStatus] = useState(null);
  const [mobile, setMobile] = useState("");

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [modalHeader, setModalHeader] = useState("");
  const [newUser, setNewUser] = useState({
    userName: "",
    password: null,
    email: "",
    fullName: "",
    mobile: "",
    roleId: "",
    status: "1",
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const clearNewUser = () => {
    setNewUser({
      userName: "",
      password: null,
      email: "",
      fullName: "",
      mobile: "",
      roleId: "",
      status: "1",
    });
  };

  const handleModal = (value) => {
    setModalHeader(value);
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setErrorMessage("");
    setSuccessMessage("");
    setModalHeader("");
    clearNewUser();
  };

  // Load dropdown roles
  useEffect(() => {
    axiosClient
      .get("/user/get-roles-for-user")
      .then((res) => setRoles(res.data || []))
      .catch((err) => console.error("Dropdown fetch failed", err));
  }, []);

  // Fetch Data
  const fetchData = () => {
    setLoading(true);
    const request = new UserRequest();
    request.setStartIndex(page * pageSize);
    request.setLimit(pageSize);

    if (sortModel.length > 0) {
      const sort = sortModel[0];
      var backendField =
        {
          id: "id",
          userName: "user_name",
          email: "email",
          fullName: "full_name",
          status: "status",
          roleName: "role_name",
        }[sort.field] || sort.field;
      backendField =
        backendField === "role_name"
          ? "r." + backendField
          : "u." + backendField;
      request.setOrderBy(
        `order by ${backendField} ${sort.sort?.toUpperCase()}`
      );
    }

    if (roleId) request.setRoleId(Number(roleId));
    if (email) request.setEmail(email);
    if (username) request.setUsername(username);
    if (status) request.setStatus(Number(status));
    if (mobile) request.setMobile(mobile);

    axiosClient
      .post("/user/get-all-user", request)
      .then((res) => {
        const userResponse = new UserResponse(res.data);
        if (userResponse?.getOperationResult() && userResponse.getUserList()) {
          setRows(userResponse.getUserList());
          setRowCount(userResponse.getCount());
        } else {
          setRows([]);
          setRowCount(0);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize, sortModel]);

  // Handle new user save
  const handleSaveUser = () => {
    setErrorMessage("");
    if (!newUser.userName || newUser.userName.length === 0) {
      setErrorMessage("Please fill all required fields. Marked With (*)");
      return;
    } else if (!newUser.password || newUser.password.length === 0) {
      setErrorMessage("Please fill all required fields. Marked With (*)");
      return;
    } else if (!newUser.fullName || newUser.fullName.length === 0) {
      setErrorMessage("Please fill all required fields. Marked With (*)");
      return;
    } else if (!newUser.email || newUser.email.length === 0) {
      setErrorMessage("Please fill all required fields. Marked With (*)");
      return;
    } else if (!newUser.mobile || newUser.mobile.length === 0) {
      setErrorMessage("Please fill all required fields. Marked With (*)");
      return;
    } else if (!newUser.roleId || newUser.roleId.length === 0) {
      setErrorMessage("Please fill all required fields. Marked With (*)");
      return;
    }
    axiosClient
      .post("/user/save", newUser) // ✅ replace with your actual endpoint
      .then((res) => {
        if (res != null && res.data != null && res.data.operationResult) {
          if (newUser.id != null) {
            setSuccessMessage("User Edited Successfully");
          } else {
            setSuccessMessage("User Saved Successfully");
          }
          setModalHeader("Create New User");
          clearNewUser();
          fetchData(); // refresh table
        } else {
          const msg = res?.data?.errorMsg || "User creation failed.";
          setErrorMessage(msg);
        }
      })
      .catch((err) => {
        console.error("User creation failed", err);
        const msg = err.response?.data?.message || "User creation failed.";
        setErrorMessage(msg);
      });
  };

  // Handle Edit button click
  const handleEditUser = (id) => {
    console.log("Edit user with ID:", id);
    setLoading(true);
    axiosClient
      .get("/user/get-by-id/" + id)
      .then((res) => {
        const user = new UserDto(res.data);
        console.log("user: " + JSON.stringify(user));
        if (user != null && user.id != null && user.id > 0) {
          setNewUser({
            id: user.getId(),
            userName: user.getUserName(),
            password: null,
            email: user.getEmail(),
            fullName: user.getFullName(),
            mobile: user.getMobile(),
            roleId: user.getRoleId(),
            status: user.getStatus(),
          });
        } else {
          setNewUser({
            id: null,
            userName: "",
            password: null,
            email: "",
            fullName: "",
            mobile: "",
            roleId: "",
            status: "1",
          });
          setErrorMessage("Couldn't Get User Data");
        }
        setModalHeader("Edit User");
        setOpenModal(true);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "userName", headerName: "User Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1 },
    { field: "fullName", headerName: "Full Name", flex: 1 },
    { field: "mobile", headerName: "Mobile No", flex: 1 },
    { field: "roleName", headerName: "Role", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      width: 90,
      renderCell: (params) => (
        <Tooltip title="Edit">
          <IconButton
            sx={{
              backgroundColor: "#1976d2", // blue background
              color: "white", // make icon white
              "&:hover": {
                backgroundColor: "#115293", // darker on hover
              },
            }}
            size="small"
            onClick={() => handleEditUser(params.row.id)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Layout title="User Management">
      {/* 🔹 Top bar with Add button */}
      <Grid
        container
        justifyContent={{ xs: "stretch", sm: "flex-end" }}
        sx={{ mb: 2 }}
      >
        <Button
          variant="contained"
          color="success"
          onClick={() => handleModal("Create New User")}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          + Add User
        </Button>
      </Grid>

      {/* 🔹 Search Form */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Grid container sx={searchGridSx}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="User Name"
              type="text"
              value={username || ""}
              onChange={(e) => setUsername(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Email"
              type="text"
              value={email || ""}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Mobile"
              type="text"
              value={mobile || ""}
              onChange={(e) => setMobile(e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              label="Status"
              value={status || ""}
              onChange={(e) => setStatus(e.target.value)}
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="1">Active</MenuItem>
              <MenuItem value="2">Inactive</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              label="Role"
              value={roleId || ""}
              onChange={(e) => setRoleId(e.target.value)}
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
            >
              <MenuItem value="">All</MenuItem>
              {roles.map((dt, i) => (
                <MenuItem key={i} value={dt.id}>
                  {dt.roleName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
              onClick={fetchData}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* 🔹 DataGrid */}
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pagination
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
          sortingOrder={["asc", "desc"]}
          loading={loading}
          pageSizeOptions={[5, 10, 20, 50]}
          autoHeight
          sx={{ minWidth: { xs: 980, sm: "100%" }, bgcolor: "white" }}
        />
      </Box>

      {/* 🔹 Add User Modal */}
      <Dialog
        open={openModal}
        onClose={() => handleModalClose()}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { m: { xs: 1.5, sm: 3 }, width: "100%" } }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#1565C0",
            color: "white",
            fontWeight: "bold",
          }}
        >
          {modalHeader}
        </DialogTitle>
        <DialogContent dividers>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          {/* Top-level Grid */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Row 1 */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="User Name *"
                fullWidth
                inputProps={{ maxLength: 50 }}
                value={newUser.userName}
                onChange={(e) =>
                  setNewUser({ ...newUser, userName: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Password *"
                type="password"
                inputProps={{ maxLength: 30 }}
                fullWidth
                value={newUser.password}
                onChange={(e) =>
                  setNewUser({ ...newUser, password: e.target.value })
                }
              />
            </Grid>

            {/* Row 2 */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Full Name *"
                fullWidth
                inputProps={{ maxLength: 100 }}
                value={newUser.fullName}
                onChange={(e) =>
                  setNewUser({ ...newUser, fullName: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email *"
                fullWidth
                inputProps={{ maxLength: 150 }}
                value={newUser.email}
                onChange={(e) =>
                  setNewUser({ ...newUser, email: e.target.value })
                }
              />
            </Grid>

            {/* Row 3 */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Mobile *"
                fullWidth
                inputProps={{ maxLength: 20 }}
                value={newUser.mobile}
                onChange={(e) =>
                  setNewUser({ ...newUser, mobile: e.target.value })
                }
              />
            </Grid>

            {/* 🔹 Role & Status on the same line */}
            <Grid container spacing={2} sx={{ width: "100%" }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Role *"
                  sx={{ minWidth: { sm: 210 } }} // ⬅️ always keeps at least 250px wide
                  fullWidth
                  value={newUser.roleId}
                  onChange={(e) =>
                    setNewUser({ ...newUser, roleId: Number(e.target.value) })
                  }
                >
                  {roles.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.roleName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  sx={{ minWidth: { sm: 210 } }} // ⬅️ always keeps at least 250px wide
                  fullWidth
                  label="Status *"
                  value={newUser.status}
                  onChange={(e) =>
                    setNewUser({ ...newUser, status: e.target.value })
                  }
                >
                  <MenuItem value="1">Active</MenuItem>
                  <MenuItem value="2">Inactive</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleModalClose()}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveUser}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
